/**
 * Tastatur-Komfort für Markdown-Textfelder: Tab rückt ein, Enter führt
 * Listen, Nummerierungen und Aufgaben (`[ ]`) fort und richtet Tabellen aus.
 * Editor und Hausaufgaben-Dialog teilen sich diese Logik, damit sich beide
 * Felder exakt gleich anfühlen.
 */

import { findTableAt, serializeTable, expandTableRows, splitRow, SEP_CELL } from "./tableEditor";
import { renumberAroundCursor } from "./orderedList";
import { scanFences } from "./folding";
import type { MdField } from "./field";

/**
 * Zeilenmarker: Aufzählung/Nummer (optional mit Aufgabe dahinter) oder eine
 * Aufgabe ganz ohne Aufzählungszeichen.
 */
const LIST_RE = /^(\s*)((?:[-*+]|\d+[.)])(?:\s+\[[ xX]\])?|\[[ xX]\])\s(.*)$/;

const GERMAN_MONTHS =
  /^(Januar|Februar|März|April|Mai|Juni|Juli|August|September|Oktober|November|Dezember)\b/;

export function attachMarkdownKeys(ta: MdField): void {
  const lineStartAt = (pos: number) => ta.value.lastIndexOf("\n", pos - 1) + 1;
  const lineEndAt = (pos: number) => {
    const i = ta.value.indexOf("\n", pos);
    return i === -1 ? ta.value.length : i;
  };
  const lineIndexAt = (pos: number) => ta.value.slice(0, pos).split("\n").length - 1;
  const inFenceAt = (pos: number) => scanFences(ta.value.split("\n")).inCode[lineIndexAt(pos)];

  /**
   * Tab auf einer Überschriftenzeile stuft sie eine Ebene tiefer
   * (`#` → `##` → `###`), Shift+Tab wieder hoch – und aus `# ` heraus zurück
   * in normalen Text. Gilt für die Zeile am Cursor, egal wo er darin steht.
   */
  function stepHeading(outdent: boolean): boolean {
    const pos = ta.selectionStart;
    if (ta.selectionEnd !== pos) return false;
    const ls = lineStartAt(pos);
    const le = lineEndAt(pos);
    const line = ta.value.slice(ls, le);
    const m = /^(#{1,6})([ \t]+)/.exec(line);
    if (!m) return false;

    const level = m[1].length;
    let next: string;
    if (outdent) {
      next = level <= 1 ? line.slice(m[0].length) : line.slice(1);
    } else {
      if (level >= 6) return true; // schon die tiefste Ebene
      next = "#" + line;
    }
    const delta = next.length - line.length;
    ta.setSelectionRange(ls, le);
    ta.insert(next);
    const caret = Math.max(ls, pos + delta);
    ta.setSelectionRange(caret, caret);
    return true;
  }

  /** Tab rückt ein (zwei Leerzeichen), Shift+Tab aus – zeilenweise bei Auswahl. */
  function indentSelection(outdent: boolean): void {
    const selStart = ta.selectionStart;
    const selEnd = ta.selectionEnd;
    const blockStart = lineStartAt(selStart);
    const blockEnd = lineEndAt(Math.max(selEnd - (selEnd > selStart ? 1 : 0), selStart));
    const block = ta.value.slice(blockStart, blockEnd);
    const multiline = block.includes("\n");
    const isList = LIST_RE.test(block.split("\n")[0] ?? "");

    // Tab im Fließtext (keine Liste, keine Auswahl): einfach einrücken am Cursor
    if (!multiline && !isList && !outdent) {
      ta.insert("  ");
      return;
    }

    const next = block
      .split("\n")
      .map((l) => (outdent ? l.replace(/^ {1,2}/, "") : "  " + l))
      .join("\n");
    if (next === block) return;
    ta.setSelectionRange(blockStart, blockEnd);
    ta.insert(next);
    const delta = next.length - block.length;
    if (selEnd > selStart) ta.setSelectionRange(blockStart, blockEnd + delta);
    else {
      const shift = outdent ? Math.max(-2, blockStart - selStart) : 2;
      ta.setSelectionRange(selStart + shift, selStart + shift);
    }
  }

  /** Enter in einer Liste: Marker fortführen (Nummern hochzählen, Haken leeren). */
  function continueList(): boolean {
    const pos = ta.selectionStart;
    if (ta.selectionEnd !== pos) return false;
    if (inFenceAt(pos)) return false;
    const ls = lineStartAt(pos);
    const m = ta.value.slice(ls, pos).match(LIST_RE);
    if (!m) return false;
    const [, indent, marker, rest] = m;
    if (/^\d+[.)]$/.test(marker) && GERMAN_MONTHS.test(rest)) return false;
    const afterCursor = ta.value.slice(pos, lineEndAt(pos));
    if (!rest.trim() && !afterCursor.trim()) {
      // Leerer Punkt + Enter = Liste verlassen (Marker entfernen)
      ta.setSelectionRange(ls, pos);
      ta.erase();
      return true;
    }
    let next = marker;
    const num = marker.match(/^(\d+)([.)])/);
    if (num) next = next.replace(/^\d+[.)]/, `${Number(num[1]) + 1}${num[2]}`);
    next = next.replace(/\[[xX]\]/, "[ ]");
    ta.insert(`\n${indent}${next} `);
    return true;
  }

  /**
   * Enter innerhalb eines eingerückten `::: … :::`-Blocks (der selbst in
   * einem Listenpunkt steckt): die Einrückung wird beibehalten. Ohne das
   * würde die Folgezeile – und damit ein am Ende getipptes `:::` – auf
   * Spalte 0 rutschen, den Container verlassen und als roher Text
   * hängen bleiben statt ihn zu schließen.
   */
  function continueIndentedBlock(): boolean {
    const pos = ta.selectionStart;
    if (ta.selectionEnd !== pos) return false;
    if (inFenceAt(pos)) return false;
    const ls = lineStartAt(pos);
    const line = ta.value.slice(ls, pos);
    const indent = /^[ \t]+/.exec(line)?.[0];
    // Nur wenn die Zeile NICHT selbst ein Listenmarker ist (das übernimmt
    // continueList) und die Einrückung mindestens 2 Spalten tief ist – d. h.
    // erkennbar „unter" einem Listenpunkt oder Container steht.
    if (!indent || indent.length < 2 || LIST_RE.test(line)) return false;
    ta.insert(`\n${indent}`);
    return true;
  }

  /**
   * Enter am Ende einer Tabellenzeile: `...` ausfüllen (series.ts), Block
   * bündig ausrichten + neue leere Zeile beginnen. Enter auf einer leeren
   * Schlusszeile beendet die Tabelle. Mitten in der Tabelle wird nur
   * ausgefüllt, falls dort ein `...` steht.
   */
  function continueTable(): boolean {
    const pos = ta.selectionStart;
    if (ta.selectionEnd !== pos) return false;
    if (inFenceAt(pos)) return false;
    const line = ta.value.slice(lineStartAt(pos), lineEndAt(pos));
    if (!/^\s*\|/.test(line)) return false;
    const block = findTableAt(ta.value, pos);
    if (!block) return startTable(line);
    // Leere Zellen am Zeilenende weg: die vorbereitete Leerzeile hat schon alle
    // Spalten – wer dort selbst `|` tippt, hätte sie sonst doppelt.
    // `serializeTable` füllt ohnehin wieder auf die Spaltenzahl auf.
    const trimmed = block.cells.map((row, i) => {
      if (i === 0) return row;
      let end = row.length;
      while (end > 1 && !row[end - 1].trim()) end--;
      return row.slice(0, end);
    });
    const cells = expandTableRows(trimmed) ?? trimmed;
    const atEnd = lineEndAt(pos) >= block.end;
    if (!atEnd) {
      if (cells === trimmed) return false; // mitten in der Tabelle: normales Enter
      ta.setSelectionRange(block.start, block.end);
      ta.insert(serializeTable(cells));
      return true;
    }

    const lastRow = cells[cells.length - 1] ?? [];
    const lastRowEmpty = cells.length > 1 && lastRow.every((c) => !c.trim());
    const cols = Math.max(...cells.map((r) => r.length), 1);

    if (lastRowEmpty) {
      const out = serializeTable(cells.slice(0, -1));
      ta.setSelectionRange(block.start, block.end);
      ta.insert(out + "\n\n");
      return true;
    }
    const out = serializeTable([...cells, Array(cols).fill("")]);
    ta.setSelectionRange(block.start, block.end);
    ta.insert(out);
    const caret = block.start + out.lastIndexOf("\n") + 3;
    ta.setSelectionRange(caret, caret);
    return true;
  }

  /**
   * Erste Zeile `| a | b | c |` + Enter: Trennzeile und eine leere Zeile
   * dazu – die Tabelle steht, ohne `|---|` von Hand. Nur am Zeilenende und
   * nur, wenn darüber/darunter keine Tabellenzeile steht.
   */
  function startTable(line: string): boolean {
    const pos = ta.selectionStart;
    if (pos !== lineEndAt(pos) || !/^\s*\|.*\|\s*$/.test(line)) return false;
    const ls = lineStartAt(pos);
    const prev = ta.value.slice(lineStartAt(Math.max(0, ls - 1)), Math.max(0, ls - 1));
    const next = ta.value.slice(pos + 1, lineEndAt(pos + 1));
    if (ls > 0 && /^\s*\|/.test(prev)) return false;
    if (/^\s*\|/.test(next)) return false;
    const head = splitRow(line);
    if (head.length < 2) return false;
    const [full] = expandTableRows([head]) ?? [head];
    const out = serializeTable([full, Array(full.length).fill("")]);
    ta.setSelectionRange(ls, pos);
    ta.insert(out);
    const caret = ls + out.lastIndexOf("\n") + 3;
    ta.setSelectionRange(caret, caret);
    return true;
  }

  /**
   * Tab in einer Tabellenzeile: zur nächsten Zelle (Inhalt markiert, wie in
   * einer Tabellenkalkulation), Shift+Tab zur vorigen. Tab in der letzten
   * Zelle der letzten Zeile hängt eine neue Zeile an.
   */
  function tableTab(back: boolean): boolean {
    const pos = ta.selectionStart;
    const ls = lineStartAt(pos);
    const line = ta.value.slice(ls, lineEndAt(pos));
    if (!/^\s*\|/.test(line) || inFenceAt(pos)) return false;
    const block = findTableAt(ta.value, pos);
    if (!block) return false;

    // Zellgrenzen aller Zeilen des Blocks (Trennzeile übersprungen).
    const cellsOf = (from: number, text: string) => {
      const pipes: number[] = [];
      for (let i = 0; i < text.length; i++) if (text[i] === "|" && text[i - 1] !== "\\") pipes.push(from + i);
      return pipes.slice(0, -1).map((p, i) => {
        const raw = ta.value.slice(p + 1, pipes[i + 1]);
        const lead = raw.length - raw.trimStart().length;
        const start = raw.trim() ? p + 1 + lead : Math.min(p + 2, pipes[i + 1]);
        return { pipe: p, next: pipes[i + 1], start, end: raw.trim() ? start + raw.trim().length : start };
      });
    };
    const rows: { pipe: number; next: number; start: number; end: number }[][] = [];
    let at = block.start;
    for (const text of ta.value.slice(block.start, block.end).split("\n")) {
      if (!splitRow(text).every((c) => SEP_CELL.test(c))) rows.push(cellsOf(at, text));
      at += text.length + 1;
    }
    const flat = rows.flat();
    // Zelle unter dem Cursor: zwischen ihrem `|` und dem nächsten. Davor/dahinter
    // (Einrückung, nach dem letzten `|`) zählt die nächstgelegene Zelle der Zeile.
    let here = flat.findIndex((c) => c.pipe < pos && pos <= c.next);
    if (here < 0) here = flat.reduce((best, c, i) => (c.pipe < pos ? i : best), 0);
    const target = flat[here + (back ? -1 : 1)];
    if (target) {
      ta.setSelectionRange(target.start, target.end);
      return true;
    }
    if (back) return true;
    // Letzte Zelle: neue Zeile wie mit Enter am Tabellenende.
    ta.setSelectionRange(block.end, block.end);
    return continueTable();
  }

  ta.on("keydown", (e: KeyboardEvent) => {
    if (e.key === "Tab" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      // Mehrere Cursor: das kann das Feld selbst am besten (CodeMirror).
      if (!ta.indentRanges?.(e.shiftKey) && !tableTab(e.shiftKey) && !stepHeading(e.shiftKey)) {
        indentSelection(e.shiftKey);
      }
      renumberAroundCursor(ta);
      ta.emitInput();
      return;
    }
    if (e.key === "Enter" && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      if (continueTable() || continueList() || continueIndentedBlock()) {
        e.preventDefault();
        renumberAroundCursor(ta);
        ta.emitInput();
      }
    }
  });
}
